import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Divider, Snackbar,
} from "@mui/material";
import { Add, Search, Refresh, Edit, Delete, Router, CheckCircle, Cancel, Sync, Hub, Star, StarBorder } from "@mui/icons-material";
import api from "../../services/api";

const PROTOCOLS = ["UDP", "TCP", "TLS", "WS", "WSS"];
const CODECS_OPTIONS = ["PCMU", "PCMA", "G722", "G729", "OPUS"];

const emptyForm = {
  name: "", code: "", host: "", port: 5060, protocol: "UDP",
  username: "", password: "", auth_type: "ip",
  codec_list: ["PCMU", "PCMA"], max_channels: 100, max_cps: 10, status: "active", srtp: "none",
};

export const CarriersPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState("");
  // ── Gateways (dual-homing) ──
  const [gwOpen, setGwOpen] = useState(false);
  const [gwCarrier, setGwCarrier] = useState<any>(null);
  const [gateways, setGateways] = useState<any[]>([]);
  const [gwLoading, setGwLoading] = useState(false);
  const emptyGw = { name: "", host: "", port: 5060, protocol: "UDP", priority: 100, weight: 100, status: "active" };
  const [gwForm, setGwForm] = useState<any>(emptyGw);
  const [gwEditing, setGwEditing] = useState<any>(null);
  const [gwSaving, setGwSaving] = useState(false);

  const loadGateways = useCallback(async (carrierId: string) => {
    setGwLoading(true);
    try { const { data } = await api.get(`/carriers/${carrierId}/gateways`); setGateways(data || []); }
    catch { setGateways([]); } finally { setGwLoading(false); }
  }, []);

  const openGateways = (carrier: any) => {
    setGwCarrier(carrier); setGwEditing(null); setGwForm(emptyGw); setGwOpen(true); loadGateways(carrier.id);
  };

  const saveGateway = async () => {
    setGwSaving(true);
    try {
      if (gwEditing) await api.put(`/carriers/gateways/${gwEditing.id}`, gwForm);
      else await api.post(`/carriers/${gwCarrier.id}/gateways`, gwForm);
      setGwEditing(null); setGwForm(emptyGw);
      await loadGateways(gwCarrier.id);
      setSnack("Gateway saved — routing synced to SBC");
    } catch (e: any) { setSnack("Gateway save failed: " + (e.response?.data?.message || e.message)); }
    finally { setGwSaving(false); }
  };

  const removeGateway = async (gw: any) => {
    try { await api.delete(`/carriers/gateways/${gw.id}`); await loadGateways(gwCarrier.id); setSnack("Gateway removed — routing synced to SBC"); }
    catch (e: any) { setSnack("Remove failed: " + (e.response?.data?.message || e.message)); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      params.set("limit", "25");
      params.set("offset", String(page * 25));
      const { data: res } = await api.get(`/carriers?${params}`);
      const rows = res.data || (Array.isArray(res) ? res : []);
      setData(rows);
      setTotal(res.total || rows.length);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(""); setDialogOpen(true); };
  const openEdit = (row: any) => {
    setEditing(row);
    setForm({ ...emptyForm, ...row, protocol: row.protocol || "UDP", codec_list: row.codec_list || ["PCMU", "PCMA"] });
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true); setError("");
    try {
      if (!form.code) form.code = form.name.toUpperCase().replace(/[^A-Z0-9]+/g, "_").substring(0, 50);
      if (editing) await api.put(`/carriers/${editing.id}`, form);
      else await api.post("/carriers", form);
      setDialogOpen(false);
      setSnack("Carrier saved — routing synced to SBC");
      load();
    } catch (e: any) {
      setError(e.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/carriers/${editing.id}`);
      setDeleteDialogOpen(false);
      setSnack("Carrier deactivated — routing synced to SBC");
      load();
    } catch (e) { console.error(e); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: res } = await api.post("/carriers/sync");
      setSnack(res.message || res.synced || "Routing synced to SBC (Kamailio reloaded)");
    } catch (e: any) {
      setSnack("Sync failed: " + (e.response?.data?.message || e.message));
    } finally { setSyncing(false); }
  };

  const toggleCodec = (codec: string) => {
    const list: string[] = form.codec_list || [];
    setForm({ ...form, codec_list: list.includes(codec) ? list.filter((c: string) => c !== codec) : [...list, codec] });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Router sx={{ color: "primary.main", fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Carriers</Typography>
            <Typography variant="body2" color="text.secondary">{total} carriers configured</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField size="small" placeholder="Search carriers..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 220 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Tooltip title="Rebuild Kamailio LCR routing (gateways + rules + ACL) from carriers and hot-reload the SBC">
            <Button variant="outlined" color="secondary" startIcon={syncing ? <CircularProgress size={16} /> : <Sync />} onClick={handleSync} disabled={syncing}>
              {syncing ? "Syncing..." : "Sync Routing to SBC"}
            </Button>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Carrier</Button>
        </Box>
      </Box>

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Name", "Code", "Host", "Port", "Protocol", "Auth", "Codecs", "Max Ch.", "Status", "Actions"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}>{row.code}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{row.host}</TableCell>
                    <TableCell>{row.port || 5060}</TableCell>
                    <TableCell><Chip label={row.protocol || "UDP"} size="small" color={row.protocol === "TLS" ? "success" : "default"} variant="outlined" /></TableCell>
                    <TableCell>{row.auth_type || "ip"}</TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>{(row.codec_list || []).join(", ")}</TableCell>
                    <TableCell>{row.max_channels || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {row.status === "active"
                          ? <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                          : <Cancel sx={{ fontSize: 16, color: "error.main" }} />}
                        <Typography variant="caption">{row.status}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Gateways (dual-homing)"><IconButton size="small" color="primary" onClick={() => openGateways(row)}><Hub fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Deactivate"><IconButton size="small" color="error" onClick={() => { setEditing(row); setDeleteDialogOpen(true); }}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary" }}>No carriers configured</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={25} onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[25]} />
          </>
        )}
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit Carrier: ${editing.name}` : "New Carrier"}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Carrier Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Code (auto if blank)" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} helperText="Unique identifier e.g. BT-UK" />
            </Grid>
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">SIP Configuration</Typography></Divider></Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="SIP Host / IP" value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} required placeholder="sip.carrier.com or 192.168.1.1" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Port" type="number" value={form.port} onChange={e => setForm({ ...form, port: parseInt(e.target.value) || 5060 })} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Protocol" value={form.protocol} onChange={e => setForm({ ...form, protocol: e.target.value, port: e.target.value === "TLS" ? 5061 : 5060 })}>
                {PROTOCOLS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Auth Type" value={form.auth_type} onChange={e => setForm({ ...form, auth_type: e.target.value })}>
                <MenuItem value="ip">IP Auth (no credentials)</MenuItem>
                <MenuItem value="digest">SIP Digest</MenuItem>
              </TextField>
            </Grid>
            {form.auth_type === "digest" && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Username" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Password" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                </Grid>
              </>
            )}
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">Codecs</Typography></Divider></Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {CODECS_OPTIONS.map(c => (
                  <Chip key={c} label={c} clickable
                    color={(form.codec_list || []).includes(c) ? "primary" : "default"}
                    onClick={() => toggleCodec(c)} />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">Capacity</Typography></Divider></Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Max Channels" type="number" value={form.max_channels} onChange={e => setForm({ ...form, max_channels: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Max CPS" type="number" value={form.max_cps} onChange={e => setForm({ ...form, max_cps: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Status" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption" color="text.secondary">Media Security</Typography></Divider></Grid>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="SRTP (media encryption toward this carrier)" value={form.srtp || "none"}
                onChange={e => setForm({ ...form, srtp: e.target.value })}
                helperText="'Require' encrypts this carrier's leg (SRTP); the customer leg stays plain RTP. RTPEngine bridges both.">
                <MenuItem value="none">None — plain RTP</MenuItem>
                <MenuItem value="require">Require SRTP</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name || !form.host}>
            {saving ? <CircularProgress size={20} /> : editing ? "Save Changes" : "Create Carrier"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete / Deactivate dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Carrier</DialogTitle>
        <DialogContent>
          <Typography>Deactivate <strong>{editing?.name}</strong>? Its gateways will be removed from SBC routing on the next sync.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* Gateways (dual-homing) dialog */}
      <Dialog open={gwOpen} onClose={() => setGwOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Hub color="primary" /> Gateways — {gwCarrier?.name}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Multiple gateways = dual-homing. LCR automatically fails over across all active gateways of this carrier.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {gwLoading ? <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={24} /></Box> : (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead><TableRow>
                {["", "Name", "Host", "Port", "Transport", "Prio", "Weight", "Status", ""].map((h, i) =>
                  <TableCell key={i} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {gateways.map(g => (
                  <TableRow key={g.id} hover>
                    <TableCell>{g.is_primary ? <Tooltip title="Primary"><Star sx={{ fontSize: 16, color: "warning.main" }} /></Tooltip> : <StarBorder sx={{ fontSize: 16, color: "text.disabled" }} />}</TableCell>
                    <TableCell>{g.name || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{g.host}</TableCell>
                    <TableCell>{g.port}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={g.protocol} color={g.protocol === "TLS" ? "success" : "default"} /></TableCell>
                    <TableCell>{g.priority}</TableCell>
                    <TableCell>{g.weight}</TableCell>
                    <TableCell><Chip size="small" label={g.status} color={g.status === "active" ? "success" : "default"} /></TableCell>
                    <TableCell>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => { setGwEditing(g); setGwForm({ name: g.name || "", host: g.host, port: g.port, protocol: g.protocol, priority: g.priority, weight: g.weight, status: g.status }); }}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title={g.is_primary ? "Can't remove the primary gateway" : "Remove"}>
                        <span><IconButton size="small" color="error" disabled={g.is_primary} onClick={() => removeGateway(g)}><Delete fontSize="small" /></IconButton></span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {gateways.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>No gateways</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">{gwEditing ? "Edit gateway" : "Add gateway"}</Typography></Divider>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Name" value={gwForm.name} onChange={e => setGwForm({ ...gwForm, name: e.target.value })} placeholder="e.g. London" /></Grid>
            <Grid item xs={6} md={3}><TextField size="small" fullWidth label="Host / IP" value={gwForm.host} onChange={e => setGwForm({ ...gwForm, host: e.target.value })} /></Grid>
            <Grid item xs={4} md={2}><TextField size="small" fullWidth label="Port" type="number" value={gwForm.port} onChange={e => setGwForm({ ...gwForm, port: parseInt(e.target.value) || 5060 })} /></Grid>
            <Grid item xs={4} md={2}><TextField size="small" select fullWidth label="Transport" value={gwForm.protocol} onChange={e => setGwForm({ ...gwForm, protocol: e.target.value })}>{PROTOCOLS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
            <Grid item xs={2} md={1}><TextField size="small" fullWidth label="Prio" type="number" value={gwForm.priority} onChange={e => setGwForm({ ...gwForm, priority: parseInt(e.target.value) || 100 })} /></Grid>
            <Grid item xs={2} md={1}><TextField size="small" fullWidth label="Wt" type="number" value={gwForm.weight} onChange={e => setGwForm({ ...gwForm, weight: parseInt(e.target.value) || 100 })} /></Grid>
            <Grid item xs={12} md={12} sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" size="small" onClick={saveGateway} disabled={gwSaving || !gwForm.host} startIcon={gwSaving ? <CircularProgress size={14} /> : <Add />}>
                {gwEditing ? "Save Gateway" : "Add Gateway"}
              </Button>
              {gwEditing && <Button size="small" onClick={() => { setGwEditing(null); setGwForm(emptyGw); }}>Cancel edit</Button>}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => { setGwOpen(false); load(); }}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack("")} message={snack} />
    </Box>
  );
};
